import { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import logger from '../lib/logger';

const EMPTY_LEAD_FORM = {
  lead_name: '', company_name: '', phone: '', email: '',
  category: '', city: '',
  source: null, setter_id: null, closer_id: null, team: null,
  total_deal_value: '', status: 'New', next_followup_date: '',
};

const phoneDigits = (v) => String(v || '').replace(/\D/g, '');

/** Strict Add Lead validation — returns {field: message} (empty when valid).
 * Name, Company/Brand Name and Mobile Number are mandatory. */
export const validateLeadForm = (form) => {
  const errors = {};
  if (!form.lead_name.trim()) errors.lead_name = 'Name is required';
  if (!form.company_name.trim()) errors.company_name = 'Company/Brand name is required';
  const digits = phoneDigits(form.phone);
  if (!digits) errors.phone = 'Mobile number is required';
  else if (digits.length < 10) errors.phone = 'Enter a valid 10-digit mobile number';
  return errors;
};

/** Builds the POST /leads payload from raw form state. */
export const buildCreatePayload = (form) => ({
  ...form,
  category: form.category.trim() || null,
  city: form.city.trim() || null,
  next_followup_date: form.next_followup_date || null,
  total_deal_value: Number(form.total_deal_value) || 0,
});

/**
 * Encapsulates Quick Add Lead behaviour:
 * - form state + field setter (clears that field's error on change)
 * - strict mandatory-field validation with inline errors
 * - real-time duplicate protection (Name / Mobile / Brand)
 * - lead creation with success/error toasts
 */
export const useQuickAddLead = ({ onCreated, onClose }) => {
  const [form, setForm] = useState(EMPTY_LEAD_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dupMatches, setDupMatches] = useState(null); // null | array of matches

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const createLead = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/leads', buildCreatePayload(form));
      onCreated(data);
      toast.success('Lead added');
      setForm(EMPTY_LEAD_FORM);
      setErrors({});
      setDupMatches(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add lead');
    } finally {
      setSaving(false);
    }
  };

  const checkDuplicates = async () => {
    const { data } = await api.get('/leads/check-duplicate', {
      params: {
        lead_name: form.lead_name.trim() || undefined,
        phone: form.phone.trim() || undefined,
        company_name: form.company_name.trim() || undefined,
      },
    });
    return data;
  };

  const submit = async () => {
    const errs = validateLeadForm(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error(Object.values(errs)[0]);
      return;
    }
    setSaving(true);
    try {
      const data = await checkDuplicates();
      if (data.is_duplicate) {
        setSaving(false);
        setDupMatches(data.matches);
        return;
      }
    } catch (e) {
      // Never block lead creation if the duplicate check itself fails —
      // log it so the issue is visible during debugging.
      logger.warn('Duplicate check failed, proceeding with creation', e);
    }
    await createLead();
  };

  return {
    form, set, errors, saving, submit, createLead,
    dupMatches, dismissDuplicates: () => setDupMatches(null),
  };
};
